/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Top-level shell for the Expense Inventory detail view.
 * Handles route/state resolution, data fetching, tab state, and edit-modal state.
 * Tabs/Sampling/Attachments (Phases 3-5) render as placeholders here and will be
 * swapped in as those components are built.
 * @module src/components/Inventory/expense/detail/ExpenseInventoryDetail
 */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useParams, withRouter } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Tab, Tabs, Paper } from '@material-ui/core';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Spinner from '@salesforce/design-system-react/components/spinner';
import { GET_EXPENSE_INVENTORY_DETAILS } from '../../../../graphql/getExpenseInventoryDetails';
import { GET_FORM_CONFIGURATIONS } from '../../../../graphql/getFormConfigurations';
import { GET_EXPENSE_INVENTORY_DETAIL_CONFIGURATIONS } from '../../../../graphql/getDetailsViewConfigurations';
import CANCEL_INVENTORY from '../../../../graphql/mutations/cancelInventory';
import SUBMIT_EXPENSE_INVENTORY from '../../../../graphql/mutations/submitExpenseInventory';
import { isStaleRecordError } from '../../../../util/optimisticLock';
import { getUserInfo } from '../../../../store/ducks/userInfo';
import {
  ADMIN_CONFIGS_INVENTORY_SCREEN_NAME,
  EXPENSE_INVENTORY_ASYNC_MESSAGES,
  EXPENSE_INVENTORY_CASE_STATUS,
  EXPENSE_INVENTORY_USER_ROLE,
  NOTIFY_TYPE,
  OPS_ACTION_TYPE,
  STALE_RECORD_MESSAGE,
} from '../../../../util/constants';
import useSnackBar from '../../../../util/customHooks/useSnackBar';
import SnackBar from '../../../Shared/SnackBar';
import ExpenseInventoryDetailHeader from './ExpenseInventoryDetailHeader';
import ExpenseInventoryCancelModal from './ExpenseInventoryCancelModal';
import SubmitReviewModal from './SubmitReviewModal';
import ExpenseInventorySampling, { ExpenseInventorySamplingHandle } from './ExpenseInventorySampling';
import ExpenseInventoryComments from './ExpenseInventoryComments';
import ExpenseInventoryForm from '../form/ExpenseInventoryForm';
import { getWriteOffFieldConfig, parseExpenseInventoryFormConfig } from '../form/expenseInventoryConfig';
import { getSamplingColumnVisibility } from './ExpenseInventorySampling/samplingColumnConfig';
import { getSvgIcon } from '../../../../util/utilityFunctions';
import {
  buildExpenseInventorySubmitHeader,
  getExpenseInventoryUserRole,
} from '../../../../util/expenseInventory';
import '../../index.scss';

export type Props = RouteComponentProps;

type RouteParams = {
  externalId?: string;
  type?: string;
  opsAction?: string;
};

const SUBMIT_EXPENSE_INVENTORY_SUCCESS_MESSAGE = 'success';

const DETAIL_TAB = {
  SAMPLING: 0,
  ATTACHMENTS: 1,
};

// Matches the Open/Failed/Processed tab styling on ExpenseInventoryList so the detail
// screen's Sampling/Attachments tabs look and behave the same way.
const TAB_STYLE = {
  minHeight: '42px', // Overrides default 48px
  height: '42px',
  margin: '0 24px 0 8px', // Optional: Adds some spacing between tabs
  fontSize: '16px', // Sets the base font size
  textTransform: 'none' as const, // Optional: Stops Material-UI from making text all-caps
  fontWeight: 500, // Regular weight for unselected tabs

  // 1. Force the layout engine to include borders inside the total height
  boxSizing: 'border-box' as const,

  // 2. Set a transparent bottom border on the base state so the tab's
  // internal content box size remains identical whether selected or not
  borderBottom: '5px solid transparent',

  // Target the built-in active state class name
  '&.Mui-selected': {
    fontWeight: 700,
    background: '#0000',
    borderBottom: '5px solid #ffb500',
    borderLeft: '1px solid #0000',
    borderRight: '1px solid #0000',
  },
};

const useStyles = makeStyles({
  tabsRoot: {
    minHeight: '42px', // Overrides default 48px
    height: '42px',
  },
  tabRoot: {
    ...TAB_STYLE,
    minWidth: '94px',
  },
});

/**
 * Sales Rep users may edit only while the case is New. Ops users may edit
 * while the case is New, but may only edit a Requested case when accessing
 * it from the Review Queue (isReview) rather than a general list tab like
 * Open. Any other status (or user type) is view only.
 */
const isEditableCaseStatus = (
  caseStatus: string | undefined,
  isSalesRepUser: boolean,
  isOpsUser: boolean,
  isReview: boolean
): boolean => {
  if (isOpsUser) {
    if (caseStatus === EXPENSE_INVENTORY_CASE_STATUS.REQUESTED) {
      return isReview;
    }
    return caseStatus === EXPENSE_INVENTORY_CASE_STATUS.NEW;
  }
  if (isSalesRepUser) {
    return caseStatus === EXPENSE_INVENTORY_CASE_STATUS.NEW;
  }
  return false;
};

const ExpenseInventoryDetail: FC<Props> = ({ location, history }) => {
  const classes = useStyles();
  const catalogIcon = getSvgIcon('catalog');
  const { state }: any = location;
  const routeParams = useParams<RouteParams>();
  const parameters: any = state || routeParams;
  const { id, externalId, type, opsAction } = parameters;

  const userInfo = useSelector(getUserInfo);
  const expenseInventoryUserRole = getExpenseInventoryUserRole(userInfo?.personas);
  const isSalesRepUser = expenseInventoryUserRole === EXPENSE_INVENTORY_USER_ROLE.SALES_REP;
  const isOpsUser = expenseInventoryUserRole === EXPENSE_INVENTORY_USER_ROLE.OPS;
  const isReview = isOpsUser && opsAction === OPS_ACTION_TYPE.REVIEW;

  // Guards against direct/deep-link navigation (e.g. typing the URL, a stale
  // bookmark, or a Global Search/Inventory Queue link) to this screen when the
  // BU config has Expense Inventory disabled. Skipped until assignedScreens has
  // loaded to avoid redirecting prematurely.
  const requiredForms = userInfo?.assignedScreens;
  const hasExpenseInventoryAccess = !!requiredForms?.find(
    (rec: any) =>
      rec.screen === ADMIN_CONFIGS_INVENTORY_SCREEN_NAME.EXPENSE_INVENTORY_CREATE && rec.isRequired
  );

  useEffect(() => {
    if (requiredForms && !hasExpenseInventoryAccess) {
      history.push('/Inventory');
    }
  }, [requiredForms, hasExpenseInventoryAccess, history]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSubmitReviewModalOpen, setIsSubmitReviewModalOpen] = useState(false);
  const [isSubmittingExpenseInventory, setIsSubmittingExpenseInventory] = useState(false);
  const [submittedCaseExternalId, setSubmittedCaseExternalId] = useState<string | null>(null);
  const [reviewState, setReviewState] = useState({
    allLinesDecided: false,
    loadingSaveDecision: false,
  });
  const samplingRef = useRef<ExpenseInventorySamplingHandle>(null);
  const isSubmittingExpenseInventoryRef = useRef(false);
  const isCancellingExpenseInventoryRef = useRef(false);

  const handleTabChange = useCallback(
    (_event: React.ChangeEvent<{}>, newTabIndex: number): void => {
      setSelectedTabIndex(newTabIndex);
    },
    []
  );

  const { open, notification, openSnackBar } = useSnackBar();

  const { data, loading, refetch } = useQuery(GET_EXPENSE_INVENTORY_DETAILS, {
    variables: { id, externalId },
    fetchPolicy: 'no-cache',
    skip: !id && !externalId,
  });

  const expenseInventoryDetail = data?.getExpenseInventoryDetails ?? null;

  // Resolves which detail header fields are hidden by BU-specific
  // "Expense Inventory - Create" config, keyed to the case's write-off type.
  const { data: configData } = useQuery(GET_FORM_CONFIGURATIONS, {
    fetchPolicy: 'cache-first',
  });
  const expenseInventoryConfig = parseExpenseInventoryFormConfig(
    (configData?.getFormConfigurations as any)?.expenseInventory ?? null
  );
  const headerFieldConfig = getWriteOffFieldConfig(
    expenseInventoryConfig,
    expenseInventoryDetail?.writeOffType
  );

  const {
    data: detailColumnConfigData,
    loading: detailColumnConfigLoading,
  } = useQuery(GET_EXPENSE_INVENTORY_DETAIL_CONFIGURATIONS, { fetchPolicy: 'cache-and-network' });
  const isSamplingColumnConfigPending =
    detailColumnConfigLoading && !detailColumnConfigData?.getDetailsViewConfigurations;
  const samplingColumnVisibility = getSamplingColumnVisibility(
    detailColumnConfigData?.getDetailsViewConfigurations?.expenseInventory,
    expenseInventoryDetail?.writeOffType
  );

  // The Review Queue's id cell passes opsAction=Assign/Review when opening this
  // screen. Assign is a read-only preview; Review is the editable assignment,
  // gated the same as the Inventory Request queue's convention.
  const isSamplingViewOnly =
    opsAction === OPS_ACTION_TYPE.ASSIGN ||
    !isEditableCaseStatus(expenseInventoryDetail?.status, isSalesRepUser, isOpsUser, isReview);

  // A case this page submitted stays view-only even if the reload after the submit fails and the
  // screen still holds the pre-submit status. Keyed by case so another case opened on the same
  // screen is not locked.
  const isSubmittedOnThisPage =
    !!expenseInventoryDetail?.caseExternalId &&
    submittedCaseExternalId === expenseInventoryDetail.caseExternalId;
  const isExpenseInventoryViewOnly = isSamplingViewOnly || isSubmittedOnThisPage;

  const [setCancelInventory, { loading: cancelInventoryLoading }] = useMutation(CANCEL_INVENTORY);
  const [setSubmitExpenseInventory, { loading: submitExpenseInventoryLoading }] = useMutation(
    SUBMIT_EXPENSE_INVENTORY
  );

  // The submitExpenseInventory resolver is synchronous: it runs the reservation/Salesforce write
  // inline and returns the terminal result directly, so this handles it like a normal
  // mutation-then-refetch instead of the async queued/polled submission contract used elsewhere.
  const handleSubmitExpenseInventory = useCallback(async (): Promise<void> => {
    if (submitExpenseInventoryLoading || isSubmittingExpenseInventoryRef.current) {
      return;
    }

    isSubmittingExpenseInventoryRef.current = true;
    setIsSubmittingExpenseInventory(true);

    try {
      const pendingTableSaves = samplingRef.current?.flushPendingTableSaves();
      if (pendingTableSaves) {
        await pendingTableSaves;
      }

      const res = await setSubmitExpenseInventory({
        variables: {
          caseId: expenseInventoryDetail?.id,
          caseExternalId: expenseInventoryDetail?.caseExternalId,
          expectedCaseVersion: expenseInventoryDetail?.caseVersion,
          header: buildExpenseInventorySubmitHeader(expenseInventoryDetail),
        },
      });

      if (res?.data?.submitExpenseInventory?.message !== SUBMIT_EXPENSE_INVENTORY_SUCCESS_MESSAGE) {
        throw new Error(EXPENSE_INVENTORY_ASYNC_MESSAGES.SUBMIT_ERROR);
      }
      setSubmittedCaseExternalId(expenseInventoryDetail?.caseExternalId ?? null);

      const refetchResult = await refetch();
      const updatedStatus = refetchResult?.data?.getExpenseInventoryDetails?.status;
      const message =
        updatedStatus === EXPENSE_INVENTORY_CASE_STATUS.REQUESTED
          ? EXPENSE_INVENTORY_ASYNC_MESSAGES.SUBMIT_REQUESTED_SUCCESS
          : EXPENSE_INVENTORY_ASYNC_MESSAGES.SUBMIT_SUCCESS;
      openSnackBar(NOTIFY_TYPE.SUCCESS, message);
    } catch (error) {
      if (isStaleRecordError(error)) {
        openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
      } else {
        const message = (error as Error)?.message || EXPENSE_INVENTORY_ASYNC_MESSAGES.SUBMIT_ERROR;
        openSnackBar(NOTIFY_TYPE.ERROR, message);
      }
    } finally {
      isSubmittingExpenseInventoryRef.current = false;
      setIsSubmittingExpenseInventory(false);
    }
  }, [
    expenseInventoryDetail,
    setSubmitExpenseInventory,
    submitExpenseInventoryLoading,
    refetch,
    openSnackBar,
  ]);

  // A Sampling change still waiting for the table to settle, or already saving, is saved before
  // the cancel: once the case is cancelled the save would still reserve its quantity. The modal's
  // Yes is never disabled, so a second Yes is ignored until this cancel settles.
  const handleCancelExpenseInventory = async (): Promise<void> => {
    if (isCancellingExpenseInventoryRef.current) {
      return;
    }
    isCancellingExpenseInventoryRef.current = true;

    try {
      await samplingRef.current?.flushPendingTableSaves();

      const mutation = {
        externalId: expenseInventoryDetail?.caseExternalId,
        requestedFrom: 'Inventory',
        expectedCaseVersion: expenseInventoryDetail?.caseVersion,
      };
      await setCancelInventory({ variables: mutation })
        .then(() => {
          setIsCancelModalOpen(false);
          refetch();
        })
        .catch(error => {
          if (isStaleRecordError(error)) {
            openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
          } else {
            openSnackBar(NOTIFY_TYPE.ERROR, 'Error: Expense Inventory cancellation error.');
          }
        });
    } finally {
      isCancellingExpenseInventoryRef.current = false;
    }
  };

  // While a Submit is in flight (from the click until it settles), Edit and Cancel are ignored, as on
  // the Inventory Request detail. The Actions menu stays enabled, so Submit does too.
  const handleEditPress = (): void => {
    if (isSubmittingExpenseInventoryRef.current) {
      return;
    }
    setIsEdit(!isEdit);
  };

  const handleCancelPress = (): void => {
    if (isSubmittingExpenseInventoryRef.current) {
      return;
    }
    setIsCancelModalOpen(true);
  };

  return (
    <IconSettings iconPath="/icons">
      {loading || isSamplingColumnConfigPending ? (
        <Spinner size="large" variant="base" assistiveText={{ label: 'Loading...' }} />
      ) : (
        <>
          {/* Covers the screen, header and Actions menu included, from the Submit click (pending
              Sampling saves included) until it settles. Fixed to the viewport so it covers wherever
              the rep has scrolled and the spinner stays on screen. The view stays mounted
              underneath, so nothing remounts and no state is lost. */}
          {isSubmittingExpenseInventory && <Spinner containerClassName="slds-is-fixed" />}
          <ExpenseInventoryDetailHeader
            detail={expenseInventoryDetail}
            fieldConfig={headerFieldConfig}
            type={type}
            opsAction={opsAction}
            isEdit={isEdit}
            viewOnly={isExpenseInventoryViewOnly}
            isReviewMode={isReview}
            reviewSubmitReady={reviewState.allLinesDecided && !reviewState.loadingSaveDecision}
            onEditPress={handleEditPress}
            onSubmitPress={(): void => {
              if (isReview) {
                setIsSubmitReviewModalOpen(true);
              } else {
                handleSubmitExpenseInventory();
              }
            }}
            onCancelPress={handleCancelPress}
          />

          <ExpenseInventoryCancelModal
            isOpen={isCancelModalOpen}
            caseNumber={expenseInventoryDetail?.caseNumber}
            loading={cancelInventoryLoading}
            onClose={(): void => setIsCancelModalOpen(false)}
            onConfirm={handleCancelExpenseInventory}
          />

          <SubmitReviewModal
            isOpen={isSubmitReviewModalOpen}
            caseNumber={expenseInventoryDetail?.caseNumber}
            loading={reviewState.loadingSaveDecision}
            onClose={(): void => setIsSubmitReviewModalOpen(false)}
            onConfirm={(): void => {
              samplingRef.current?.submitReview();
              setIsSubmitReviewModalOpen(false);
            }}
          />

          {isEdit && (
            <ExpenseInventoryForm
              isOpen={isEdit}
              toggleOpen={(): void => setIsEdit(false)}
              refetch={refetch}
              expenseInventoryDetail={expenseInventoryDetail}
            />
          )}

          {/* TODO (Phase 5): replace with an ExpenseInventoryTabs component that receives
              selectedTabIndex, onSelect, detail, and a refetch callback. */}
          <div className='wrapper'>
            <Paper>
              <Tabs
                TabIndicatorProps={{ style: { display: 'none' } }}
                className="inventory-tabs-scoped slds-tabs_scoped__nav"
                classes={{ root: classes.tabsRoot }}
                value={selectedTabIndex}
                onChange={handleTabChange}
                aria-label="expense_inventory_detail_tab_selection"
              >
                <Tab classes={{ root: classes.tabRoot }} label="Sampling" disableRipple />
                {/* <Tab classes={{ root: classes.tabRoot }} label="Attachments" disableRipple /> */}
                <Tab
                  classes={{ root: classes.tabRoot }}
                  disabled
                  disableRipple
                  onClick={(e: React.MouseEvent): void => e.preventDefault()}
                  style={{ cursor: 'default', marginLeft: 'auto' }}
                  label={
                    <div style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default', color: '#000000' }}>
                      <img
                        src={catalogIcon}
                        className="logo-image"
                        alt="catalog"
                        style={{ marginLeft: '8px', marginRight: '8px' }}
                      />
                      Catalog
                    </div>
                  }
                />
              </Tabs>
            </Paper>
            {selectedTabIndex === DETAIL_TAB.SAMPLING && (
              <ExpenseInventorySampling
                ref={samplingRef}
                expenseInventoryDetail={expenseInventoryDetail}
                refetch={refetch}
                viewOnly={isExpenseInventoryViewOnly || isSubmittingExpenseInventory}
                isOpsUser={isOpsUser}
                isReview={isReview}
                columnVisibility={samplingColumnVisibility}
                onReviewStateChange={setReviewState}
              />
            )}
            {selectedTabIndex === DETAIL_TAB.ATTACHMENTS && <p></p>}
            <ExpenseInventoryComments caseExternalId={expenseInventoryDetail?.caseExternalId} />
          </div>
        </>
      )}
      <SnackBar open={open} notification={notification} />
    </IconSettings>
  );
};

export default withRouter(ExpenseInventoryDetail);
